import { useContext, useEffect, useMemo, useState } from "react";
import { DialogActions } from "@mui/material";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import type { InferType } from "yup";

import type {
  FormIdAndVersion,
  GetFormDraftResponse,
  HospitalMhlAutoRouteConfig,
  SelectedFormsObject,
  SignDraftRequest,
  ValidationOutcome,
} from "@thalamos/aspire-common";
import {
  canLaunchForm,
  formIsApplication,
  formIsMedRec,
  formIsRecordOfDetention,
  formIsUnderRedo,
  formIsValidForProgression,
  formIsVoid,
  getBaseFormTemplate,
  getFullFormName,
  getHospitalIdsFromPart1Data,
  isGuestUserSession,
  redoBasisIsValid,
  redoBasisUpstreamIds,
  supportingFormsBypassDeclared,
  willFormBeCompleteAfterSigning,
} from "@thalamos/aspire-common";

import { api, apiHooks } from "../../api.js";
import { MultiPagePdf } from "../../components/MultiPagePdf.js";
import { LoggedInUserContext } from "../../Contexts.js";
import {
  isNotFoundStatus,
  isRecordId,
  throwUnansweredReadFailure,
} from "../../helpers/recordRead.js";
import { usePatientTimeline } from "../../hooks/apiCalls.js";
import { routeFns } from "../../routePaths.js";
import type { DefaultPageProps } from "../defaultProps.js";
import { applicationBasisInRedo } from "../FormProgressPage/helpers/formContextLogic.js";
import { FormSupportingDocumentsSelector } from "./FormSupportingDocumentsSelector.js";
import { onHappyWithSignature } from "./helpers/onHappyWithSignature.js";
import {
  Banner,
  BannerList,
  Button,
  LoadingSpinner,
  PopupDialog,
  PopupDialogTitle,
  renderErrorToast,
  renderInfoToast,
  Stepper,
} from "~/components/design-system/index.js";
import {
  ConfirmDialog,
  FormFooterSection,
  SignatureDialog,
} from "~/components/form/index.js";
import type { MhlAutoRouteTeam } from "~/components/form/SignatureDialog/SignatureDialog.js";
import type {
  newFormAssigneeData,
  NewResultWithoutAccreditation,
} from "~/components/FormAssignee/NewFormAssigneeDialog.js";
import { NewFormAssigneeDialog } from "~/components/FormAssignee/NewFormAssigneeDialog.js";
import { PatientBanner } from "~/components/layout/index.js";
import { useGetNhsNumber } from "~/hooks/ExternalPatientLink/useGetNhsNumber.js";
import {
  actions,
  getTemplate,
} from "~/pages/FormProgressPage/helpers/requestWorkItemActions.js";
import { bannerLogic } from "~/pages/FormProgressPage/helpers/RequestWorkItemDialog.js";

// This page supports three views/modes:
//   - "signature"          This is the primary mode which allows the user to either draw or upload a signature to sign the form
//   - "confirmation"       This is used instead of the signature view for ACD forms which do not require a signature
//   - "document-selection" This is used *in addition* to the signature mode for forms that require "supporting documents"
//                          to be attached to them (for example, Application for Detention" forms (A6/A6/A10), which require
//                          Medical Recommendations to be attached to them). It is shown prior to the signature screen.
type SigningMode = "confirmation" | "signature" | "document-selection";

// An application rests on medical recommendations and a record of detention on
// an application, so each names a different basis to reselect. A form that
// rests on nothing has nothing to warn about.
function redoBasisWarningFor(
  form: { formTemplate: FormIdAndVersion },
  t: TFunction,
): string | undefined {
  if (formIsApplication(form)) {
    return t(
      "pages.formDraftSignPage.supportingDocuments.redoBasisWarning.medicalRecommendations",
    );
  }
  if (formIsRecordOfDetention(form)) {
    return t(
      "pages.formDraftSignPage.supportingDocuments.redoBasisWarning.application",
    );
  }
  return undefined;
}

function FormDraftSignPageInner({
  formDraft,
  pdfData,
}: {
  formDraft: GetFormDraftResponse<any>;
  pdfData: string;
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const user = useContext(LoggedInUserContext)!.user;
  const isGuest = isGuestUserSession(user);

  // Signing state
  const formId = formDraft.formId;
  const [signing, setSigning] = useState(false);
  const [saveDefaultSignature, setSaveDefaultSignature] = useState(false);

  const [saveSignature, setSaveSignature] = useState<string | null | undefined>(
    null,
  );

  // Load patient (+ watch for changes via pusher)
  const patientHook = apiHooks.patients.get(formDraft.patientId);
  const [{ data: patient, loading: patientLoading }] = patientHook;

  const { patientTimeline, reloadPatientTimeline } = usePatientTimeline({
    patientId: formDraft?.patientId,
  });

  const nhsNumber = useGetNhsNumber({ patientId: formDraft.patientId });

  // Template data
  const isAmend = formDraft.formVersion > 1;
  const template = getBaseFormTemplate(formDraft);
  if (template === null) {
    throw new Error(
      `Unknown form template ${formDraft.formTemplate.id} v${formDraft.formTemplate.version}`,
    );
  }

  const partTemplate = template.parts[formDraft.part - 1];
  if (partTemplate === undefined) {
    throw new Error(`Form template has no part ${String(formDraft.part)}`);
  }

  const signingRequirements = partTemplate.signing;
  const requiresSignature = !isAmend && !signingRequirements.confirmationOnly;
  const linkableForms = signingRequirements.linkableForms;

  // Redo (non-s15) linking. Applications/H3s normally link their basis at launch
  // and do not re-prompt at sign (confirmAfterCompletion is false). Two redo cases
  // need the supporting-docs step forced here so the author (re)selects a valid
  // basis: a redo replacement (created with no links), and an in-flight form whose
  // basis was redone and replaced (voided) and must be re-linked to the new one.
  const formBeingSigned = formDraft.formContext.forms.find(
    (f) => f.id === formDraft.formId,
  );
  const isRedoReplacement = !!formBeingSigned?.redoneFromFormId;
  // Links to forms this one relies on as its basis (it is supported-by / it
  // acknowledges them).
  const basisLinks = (formBeingSigned?.linkedForms ?? []).filter(
    (l) => l.reason === "acknowledges" || l.reason === "supported-by",
  );
  const basisFormFor = (linkId: string) =>
    formDraft.formContext.forms.find((f) => f.id === linkId);
  // A basis still being redone (requested / in progress) — wait for it to resolve.
  const basisInRedo = basisLinks.some((l) => {
    const b = basisFormFor(l.id);
    return !!b && formIsUnderRedo(b);
  });
  // A basis that was redone and replaced (voided) — re-link the new version.
  const basisVoid = basisLinks.some((l) => {
    const b = basisFormFor(l.id);
    return !!b && formIsVoid(b);
  });
  // Every form this one rests on, including any whose link was severed when it
  // entered redo: that record outlives the upstream's voiding, and is the only
  // trace an application keeps of the med rec it rested on — exactly when the
  // author has to relink.
  const basisUpstreams = formBeingSigned
    ? redoBasisUpstreamIds(formBeingSigned, "basis").flatMap((id) => {
        const upstream = basisFormFor(id);
        return upstream
          ? [{ status: upstream.status, redoState: upstream.redoState ?? null }]
          : [];
      })
    : [];
  // An application's "supported-by" link to a med rec is auto-severed when the
  // med rec is redone, so basisInRedo (which keys off retained links) misses it —
  // unlike an H3, whose retained "acknowledges" link is caught above. Detect a
  // med rec in the admission being redone directly so an application gets the
  // same "basis being redone" warning an H3 does. The submit it gates is gated
  // through `signBlockedByRedoBasis`, which a declared bypass can lift.
  //
  // A redo replacement is exempt: it is created with no links and picks a fresh
  // basis in the modal from valid (completed, non-redo) forms only, so an
  // unrelated med rec still being redone elsewhere must not block it. This is
  // the escape hatch — the author redoes their own form while an upstream is
  // mid-redo and relinks to a different valid basis; the selection's own
  // validity, not admission-wide redo state, is what gates signing here.
  const blockedByRedoBasis =
    basisInRedo ||
    (!isRedoReplacement &&
      applicationBasisInRedo(formBeingSigned, formDraft.formContext.forms));
  // A med rec redo severs an in-progress application's "supported-by" basis link
  // (and does not restore it on completion), so the application can be left
  // linked to too few med recs to form a valid combination. An incomplete
  // combination is only a warning (non-blocking), so such an application would
  // otherwise sign on the reduced basis. When the admission shows a med rec has
  // been redone (voided or under redo) and the surviving validly-linked basis no
  // longer forms a complete combination, force the supporting-docs step so the
  // author re-links a valid completed combination. Scoped to applications and to
  // admissions with a redone med rec, so a clean "completed on paper" bypass is
  // never forced to relink.
  const validBasisForms = basisLinks
    .map((l) => basisFormFor(l.id))
    .filter(
      (b): b is NonNullable<typeof b> => !!b && formIsValidForProgression(b),
    );
  const admissionMedRecRedone = formDraft.formContext.forms.some(
    (f) => formIsMedRec(f) && (formIsVoid(f) || formIsUnderRedo(f)),
  );
  const applicationBasisReducedByRedo =
    !!linkableForms &&
    !!formBeingSigned &&
    formIsApplication(formBeingSigned) &&
    admissionMedRecRedone &&
    !linkableForms
      .checkValidityFn(validBasisForms)
      .some((o) => o.type === "success");
  // An in-progress form whose entire basis was severed can be left requiring a
  // basis it no longer links to (e.g. an H3 that lost its only application).
  // Force the relink step when the empty selection is itself invalid.
  const missingRequiredBasis =
    !!linkableForms &&
    basisLinks.length === 0 &&
    linkableForms.checkValidityFn([]).some((o) => o.type === "error");
  // A basis a redo has taken out of use: still being redone, or already voided
  // by one that completed. Either way this form rests on something it cannot
  // use and the author has to pick a replacement.
  const basisWithdrawnByRedo =
    blockedByRedoBasis || !redoBasisIsValid(basisUpstreams);
  const redoBasisWarning = redoBasisWarningFor(formDraft, t);
  const requiresSupportingForms =
    !isAmend &&
    !!linkableForms &&
    (!!linkableForms.confirmAfterCompletion ||
      isRedoReplacement ||
      blockedByRedoBasis ||
      basisVoid ||
      missingRequiredBasis ||
      applicationBasisReducedByRedo);

  const fullFormName = getFullFormName(template);

  const [searchResults, setSearchResults] = useState<
    NewResultWithoutAccreditation[]
  >([]);

  const [shouldShowSendDialog, setShouldShowSendDialog] =
    useState<boolean>(false);

  const [shouldShowWarningDialog, setShouldShowWarningDialog] =
    useState<boolean>(false);

  const signingRequirementShowSendDialog =
    signingRequirements.userOrTeamRequirement.requireSendingWhenSigning;

  // Redo (non-s15): completing a redo replacement should NOT prompt the
  // post-sign "send" dialog (DEV-2418). The original was already sent when first
  // completed; the redo just supersedes it in place, so re-sending is noise.
  const showSendDialog =
    shouldShowSendDialog &&
    signingRequirementShowSendDialog &&
    !isRedoReplacement;

  // Signing mode
  let initialMode: SigningMode = "confirmation";
  if (requiresSignature) initialMode = "signature";
  if (requiresSupportingForms) initialMode = "document-selection";
  const [signingMode, setSigningMode] = useState<SigningMode>(initialMode);
  const qs = new URLSearchParams(window.location.search);
  const requestType = qs.get("requestType");

  const availableActions = actions(formDraft.formContext, user);

  // Supporting documents state
  const [supportingFormsProvided, setSupportingFormsProvided] =
    useState<boolean>(false);
  const initiallySelectedForms = formDraft.linkedForms
    .filter((f) => f.reason === "supported-by")
    .map((f) => ({ id: f.id, templateId: f.formTemplate.id }));

  const [selectedForms, setSelectedForms] = useState<SelectedFormsObject[]>(
    initiallySelectedForms,
  );

  const [notSelectedReason, setNotSelectedReason] = useState<string>("");
  const [validationOutcome, setValidationOutcome] = useState<
    ValidationOutcome[] | null
  >(null);

  const [recipients, setRecipients] = useState<
    InferType<typeof newFormAssigneeData>[]
  >([]);

  const { guestAccreditationOptions } = bannerLogic(
    user,
    formDraft.formContext,
    fullFormName,
    template,
    requestType!,
  );

  // Preserve selected forms even in bypass mode so they can be included
  // in a "partially-provided" supportingFormsDetails when some med recs
  // are linked digitally and others are marked as done on paper.
  const filteredSelectedForms = selectedForms;

  const canContinueOrStartForm = canLaunchForm({
    linkableForms,
    supportingFormsProvided,
    notSelectedReason,
    validationOutcome,
    requiresSupportingForms,
  });

  // A declared bypass lifts the redo block: the author states the basis was
  // completed outside eMHA, so there is no digital dependency left to wait
  // on, and the form is not held up by a redo of a basis it no longer rests on.
  // `requiresSupportingForms` still forces the step, so the author sees what was
  // withdrawn before choosing between re-linking and declaring.
  const signBlockedByRedoBasis =
    blockedByRedoBasis &&
    !supportingFormsBypassDeclared({
      linkableForms,
      bypassToggleOn: supportingFormsProvided,
      notSelectedReason,
    });

  // MHL Auto-Route Configuration
  const mhlAutoRouteConfig =
    user.sessionOrganisationConfiguration?.mhlAutoRouteConfig;

  // Determine if signing this part will complete the form (for hospital-based MHL auto-routing)
  // Uses template.isCompleteFn if available, otherwise checks if this is the last part.
  // Can also be overridden via template.hospitalMhlAutoRoutePartOverride.
  const existingPartData = formDraft.previousPartsFormData ?? [];
  const override = template.hospitalMhlAutoRoutePartOverride;
  const willFormBeComplete =
    override != null && override.length > 0
      ? override.includes(formDraft.part)
      : willFormBeCompleteAfterSigning(
          template,
          formDraft.part,
          existingPartData,
          formDraft.data,
        );

  // State for hospital MHL configs
  const [hospitalMhlConfigs, setHospitalMhlConfigs] = useState<
    {
      hospitalId: string;
      hospitalName: string;
      config: HospitalMhlAutoRouteConfig;
    }[]
  >([]);

  // Fetch hospital MHL configs when the form will be complete after signing (not for amends)
  // Hospital IDs are always extracted from Part 1 data
  useEffect(() => {
    // Only fetch when signing this part will complete the form (not for amends or training forms)
    if (
      !willFormBeComplete ||
      isAmend ||
      formDraft.formContext.isTrainingFormContext
    ) {
      setHospitalMhlConfigs([]);
      return;
    }

    // Get Part 1 data - if we're on Part 1, use current draft data; otherwise get from form versions
    let part1Data: Record<string, unknown> | undefined;
    if (formDraft.part === 1) {
      part1Data = formDraft.data as Record<string, unknown>;
    } else {
      // For later parts, Part 1 data is in previousPartsFormData
      part1Data = formDraft.previousPartsFormData?.[0]?.data as
        Record<string, unknown> | undefined;
    }

    if (!part1Data) {
      setHospitalMhlConfigs([]);
      return;
    }

    const hospitalIds = getHospitalIdsFromPart1Data(template.id, part1Data);

    if (hospitalIds.length === 0) {
      setHospitalMhlConfigs([]);
      return;
    }

    api.configuration
      .getLocationsByIds(hospitalIds)
      .then((response) => {
        const locations = response.data;
        const configs = locations
          .filter((loc) => loc.mhlAutoRouteConfig?.teams?.length)
          .map((loc) => ({
            hospitalId: loc.id,
            hospitalName: loc.name,
            config: loc.mhlAutoRouteConfig as HospitalMhlAutoRouteConfig,
          }));
        setHospitalMhlConfigs(configs);
      })
      .catch(() => {
        setHospitalMhlConfigs([]);
      });
  }, [
    formDraft.part,
    formDraft.data,
    formDraft.previousPartsFormData,
    template.id,
    isAmend,
    willFormBeComplete,
    formDraft.formContext.isTrainingFormContext,
  ]);

  // Build the combined MHL auto-route teams array with reasons
  // Org-config teams show on Part 1, hospital-config teams show when form will be complete
  const mhlAutoRouteTeams: MhlAutoRouteTeam[] = useMemo(() => {
    if (isAmend) {
      return [];
    }

    const teams: MhlAutoRouteTeam[] = [];
    const addedTeamIds = new Set<string>();

    // Add organisation-level teams on Part 1 (if form template is enabled)
    if (
      formDraft.part === 1 &&
      mhlAutoRouteConfig?.enabledFormTemplates?.includes(template.id) &&
      mhlAutoRouteConfig.teams?.length
    ) {
      for (const team of mhlAutoRouteConfig.teams) {
        if (!addedTeamIds.has(team.teamId)) {
          teams.push({
            teamId: team.teamId,
            reason: { type: "organisation" },
          });
          addedTeamIds.add(team.teamId);
        }
      }
    }

    // Add hospital-level teams when signing this part will complete the form (not for training forms)
    if (willFormBeComplete && !formDraft.formContext.isTrainingFormContext) {
      for (const hospitalConfig of hospitalMhlConfigs) {
        for (const team of hospitalConfig.config.teams) {
          if (!addedTeamIds.has(team.teamId)) {
            teams.push({
              teamId: team.teamId,
              reason: {
                type: "hospital",
                hospitalName: hospitalConfig.hospitalName,
              },
            });
            addedTeamIds.add(team.teamId);
          }
        }
      }
    }

    return teams;
  }, [
    formDraft.part,
    isAmend,
    mhlAutoRouteConfig,
    template.id,
    hospitalMhlConfigs,
    willFormBeComplete,
    formDraft.formContext.isTrainingFormContext,
  ]);

  // Don't render anything until the patient has loaded
  if (!patient) {
    if (patientLoading) {
      return <LoadingSpinner />;
    } else {
      return <div>Error - could not find patient.</div>;
    }
  }

  return (
    <>
      <PatientBanner
        patient={patient}
        nhsNumber={nhsNumber ?? undefined}
        patientTimeline={patientTimeline}
        reloadPatientTimeline={reloadPatientTimeline}
      />

      {signingMode === "document-selection" && (
        <>
          {/* The warning asks for a valid basis to be reselected, so it is
              shown with the selector it refers to — not for the form as a
              whole, where the guidance would have nothing to act on. */}
          {basisWithdrawnByRedo && redoBasisWarning !== undefined && (
            <Banner
              sx={{ mb: 2 }}
              body={[redoBasisWarning]}
              bannerType={BannerList.WARNING}
            />
          )}

          {patientTimeline && (
            <FormSupportingDocumentsSelector
              patientTimeline={patientTimeline}
              formContext={formDraft.formContext}
              linkableForms={linkableForms}
              dialogType={
                formIsRecordOfDetention(formDraft)
                  ? "record-of-detention"
                  : undefined
              }
              selectedForms={filteredSelectedForms}
              setSelectedForms={setSelectedForms}
              notSelectedReason={notSelectedReason}
              setNotSelectedReason={setNotSelectedReason}
              supportingFormsProvided={supportingFormsProvided}
              setSupportingFormsProvided={setSupportingFormsProvided}
              setValidationOutcome={setValidationOutcome}
            />
          )}
          <FormFooterSection
            isForcedSticky={true}
            isSticky
            disableSubmit={!canContinueOrStartForm || signBlockedByRedoBasis}
            onCancel={() => {
              navigate(
                routeFns.formDraftsComplete(formDraft.id, formDraft.patientId),
              );
            }}
            onSave={() => {
              setSigningMode("signature");
            }}
            discardLabel={t("buttonLabels.goBack")}
            saveLabel={t("buttonLabels.preview")}
          />
        </>
      )}

      {signingMode !== "document-selection" && (
        <>
          {signingMode === "confirmation" && (
            <ConfirmDialog
              closeDialog={() => {
                setSigning(false);
              }}
              isOpen={signing}
              formTitleSubtitleText={
                isAmend
                  ? t(
                      "pages.formDraftPreviewPage.popupDialog.formTitleSubtitleTextAmends",
                    )
                  : t(
                      "pages.formDraftPreviewPage.popupDialog.formTitleSubtitleText",
                    )
              }
              warningMessage={
                isAmend
                  ? t(
                      "pages.formDraftPreviewPage.popupDialog.warningMessageAmends",
                    )
                  : t("pages.formDraftPreviewPage.popupDialog.warningMessage")
              }
              onConfirm={() =>
                onHappyWithSignature({
                  signature: undefined,
                  requiresSupportingForms,
                  isSupportingFormsProvided: supportingFormsProvided,
                  allSelectedForms: filteredSelectedForms.map((s) => s.id),
                  reason: notSelectedReason,
                  formId,
                  navigate,
                  formDraft,
                  template,
                  requiresSignature,
                  saveDefaultSignature,
                  user,
                  mhlAutoRouteTeams,
                })
              }
            />
          )}
          {signingMode === "signature" && signing && (
            <SignatureDialog
              onClose={() => {
                setSigning(false);
              }}
              onHappyWithSignature={(signature) => {
                // This will show the send dialog if the specific form
                // (check the form builder for the requireSendingWhenSigning check) requires it
                if (signingRequirementShowSendDialog) {
                  setSaveSignature(signature);
                  setShouldShowSendDialog(true);
                  return;
                } else {
                  return onHappyWithSignature({
                    signature,
                    requiresSupportingForms,
                    isSupportingFormsProvided: supportingFormsProvided,
                    allSelectedForms: selectedForms.map((s) => s.id),
                    reason: notSelectedReason,
                    formId,
                    navigate,
                    formDraft,
                    template,
                    requiresSignature,
                    saveDefaultSignature,
                    user,
                    mhlAutoRouteTeams,
                  });
                }
              }}
              userDefaultSignature={user.defaultSignature}
              confirmButtonLabel={t("buttonLabels.confirm")}
              clearButtonLabel={t("buttonLabels.clear")}
              saveDefaultSignature={saveDefaultSignature}
              setSaveDefaultSignature={setSaveDefaultSignature}
              allowUseOfDefaultSignature={!isGuest}
              mhlAutoRouteTeams={mhlAutoRouteTeams}
            />
          )}

          {shouldShowWarningDialog && (
            <PopupDialog maxWidth="sm" open={true} testId="request-sign-dialog">
              <PopupDialogTitle
                titleText={
                  formDraft.formContext.type === "admission"
                    ? t("common.sendInvite")
                    : t("common.sendForm")
                }
                closeDialog={() => {
                  setShouldShowSendDialog(false);
                  setShouldShowWarningDialog(true);
                  navigate(
                    routeFns.formDraftsSign(formDraft.id, formDraft.patientId),
                  );
                }}
              />
              <Banner
                sx={{ mb: 4 }}
                bannerType={BannerList.WARNING}
                title="WARNING"
                body={[
                  "Please complete process by sending form to recipients. Changes will be lost if you exit the process now",
                ]}
              />

              <DialogActions sx={{ display: "flex" }}>
                <Button
                  label="Exit"
                  testId="close-button"
                  onClick={() => {
                    setShouldShowWarningDialog(false);
                    setSaveSignature(null);
                    navigate(
                      routeFns.formDraftsSign(
                        formDraft.id,
                        formDraft.patientId,
                      ),
                    );
                  }}
                  variant="outlined"
                />
                <Button
                  label="Back"
                  testId="confirm-button"
                  onClick={() => {
                    setShouldShowWarningDialog(false);
                    setShouldShowSendDialog(true);
                  }}
                />
              </DialogActions>
            </PopupDialog>
          )}

          {showSendDialog && (
            <PopupDialog maxWidth="lg" open={true} testId="request-sign-dialog">
              <PopupDialogTitle
                titleText={
                  formDraft.formContext.type === "admission"
                    ? t("common.sendInvite")
                    : t("common.sendForm")
                }
                closeDialog={() => {
                  setShouldShowSendDialog(false);
                  setShouldShowWarningDialog(true);
                  navigate(
                    routeFns.formDraftsSign(formDraft.id, formDraft.patientId),
                  );
                }}
              />
              <Banner
                sx={{ mb: 4 }}
                body={[
                  `You have successfully signed part 1. Send ${fullFormName} to another team to complete part ${formDraft.part + 1}`,
                  `Part ${formDraft.part + 1} to be completed by someone acting on behalf of the responsible hospital. <b> Please complete the process by sending form to recipients. Changes will be lost if you do not send to recipients now.</b>`,
                ]}
              />
              <NewFormAssigneeDialog
                recipients={recipients}
                setRecipients={setRecipients}
                formId={formId}
                formContext={formDraft.formContext}
                reloadFormContext={() => {}}
                availableActions={availableActions}
                searchAssignmentOptions={async (formDetails, searchValue) => {
                  return api.formTemplates.searchAssignmentOptions({
                    id: formDetails.template.id,
                    version: formDetails.template.version,
                    query: searchValue,
                    part: formDetails.part,
                  });
                }}
                formDetails={getTemplate(formDraft.formContext)}
                userId={user.id}
                userEmail={user.email}
                userTeamId={user.sessionContext?.teamId}
                userDataControllerId={user.sessionContext?.dataControllerId}
                userDataControllerName={user.sessionContext?.dataControllerName}
                dataControllerWarningEnabled={
                  // Exempt guest, soad, and independent sender contexts from the
                  // data controller warning — they operate outside the normal
                  // data-controller model so the banner is not meaningful.
                  user.sessionContext?.teamType !== "guest" &&
                  user.sessionContext?.teamType !== "soad" &&
                  user.sessionContext?.teamType !== "independent" &&
                  (user.sessionOrganisationConfiguration
                    ?.dataControllerWarningEnabled ??
                    false)
                }
                searchResults={searchResults}
                setSearchResults={setSearchResults}
                onSave={async (
                  _formData: InferType<typeof newFormAssigneeData>,
                ) => {}}
                navigate={navigate}
                guestAccreditationOptions={guestAccreditationOptions}
                signForm={async () => {
                  if (saveSignature) {
                    const supportingFormsDetails = requiresSupportingForms
                      ? supportingFormsProvided
                        ? selectedForms.length > 0
                          ? {
                              type: "partially-provided" as const,
                              formIds: selectedForms.map((s) => s.id),
                              reason: notSelectedReason,
                            }
                          : {
                              type: "not-provided" as const,
                              reason: notSelectedReason,
                            }
                        : {
                            type: "provided" as const,
                            formIds: selectedForms.map((s) => s.id),
                          }
                      : undefined;

                    const signDraftRequest: SignDraftRequest = {
                      draftId: formDraft.id,
                      signatureBase64: saveSignature,
                      supportingFormsDetails,
                    };

                    const result = await api.forms.sign(
                      formId,
                      signDraftRequest,
                    );

                    if (result.status === 204) {
                      if (saveDefaultSignature && !user.defaultSignature)
                        await api.users.update(user.id, {
                          defaultSignature: saveSignature,
                        });
                    } else {
                      const reason = (
                        result.data as unknown as { reason?: string }
                      )?.reason;
                      renderErrorToast({ message: reason ?? "Signing failed" });
                    }
                  }
                }}
              />
            </PopupDialog>
          )}

          <Stepper
            steps={[
              t("pages.formDraftFlow.standardFlow.stepOne"),
              t("pages.formDraftFlow.standardFlow.stepTwo"),
              t("pages.formDraftFlow.standardFlow.stepThree"),
            ]}
            activeStep={1}
          />
          <MultiPagePdf data={pdfData} />
          <FormFooterSection
            isForcedSticky={true}
            isSticky
            disableSubmit={!canContinueOrStartForm || signBlockedByRedoBasis}
            onCancel={() => {
              if (requiresSupportingForms) {
                setSigningMode("document-selection");
              } else {
                navigate(
                  routeFns.formDraftsComplete(
                    formDraft.id,
                    formDraft.patientId,
                  ),
                );
              }
            }}
            onSave={() => {
              setSigning(true);
            }}
            discardLabel={t("buttonLabels.goBack")}
            saveLabel={
              requiresSignature
                ? t("buttonLabels.sign")
                : t("buttonLabels.confirm")
            }
          />
        </>
      )}
    </>
  );
}

export function FormDraftSignPage({}: DefaultPageProps) {
  const { formDraftId, patientId } = useParams();

  if (!isRecordId(formDraftId) || !isRecordId(patientId)) {
    return <Navigate to={routeFns.notFound()} replace />;
  }

  return <FormDraftSignPageForId />;
}

function FormDraftSignPageForId() {
  const { formDraftId, patientId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [
    {
      data: formDraft,
      loading: formDraftLoading,
      response: formDraftResponse,
      error: formDraftError,
    },
  ] = apiHooks.drafts.get(formDraftId!);

  const [
    {
      data: formDraftPdf,
      loading: formDraftPdfLoading,
      response: formDraftPdfResponse,
      error: formDraftPdfError,
    },
  ] = apiHooks.drafts.getPdf(formDraftId!, false);

  useEffect(() => {
    if (!formDraftLoading && !formDraftPdfLoading) {
      if (
        isNotFoundStatus(formDraftResponse?.status) ||
        isNotFoundStatus(formDraftPdfResponse?.status)
      ) {
        renderInfoToast({
          message: t("pages.formDraftFlow.draftUnavailable"),
        });
        navigate(
          patientId ? routeFns.patientHome(patientId) : routeFns.notFound(),
          { replace: true },
        );
      }
    }
  }, [
    formDraft,
    formDraftLoading,
    formDraftPdfLoading,
    formDraftPdfResponse?.status,
    formDraftResponse?.status,
    navigate,
    patientId,
    t,
  ]);

  throwUnansweredReadFailure(
    { error: formDraftError, response: formDraftResponse },
    "draft",
  );
  // A draft already known to be not found outranks its PDF's failure; one
  // still in flight does not hold the PDF's failure back.
  if (!isNotFoundStatus(formDraftResponse?.status)) {
    throwUnansweredReadFailure(
      { error: formDraftPdfError, response: formDraftPdfResponse },
      "draft's PDF",
    );
  }

  // Signing attests to the form the preview shows, so the page needs both reads.
  if (!formDraft?.id || !formDraftPdf) {
    return <></>;
  }

  return (
    <FormDraftSignPageInner formDraft={formDraft} pdfData={formDraftPdf} />
  );
}
